import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import SentEmailsTab from './SentEmailsTab';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { html as htmlLanguage } from '@codemirror/lang-html';
import s from './MailTab.module.sass';
import { notifyError, notifyWarning, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';
import FlagIcon from '../../FlagIcon/FlagIcon';
import { getFlagInfo } from '../../../config/flags';
import { getCachedConfigFlags, loadConfigFlags } from '../../../utils/configFlagCache';
import { pluralConfigs } from '../../../utils/plural';

// Группы доступных переменных для вставки в шаблон
const getVariableGroups = (siteSettings) => {
    const variables = [
        {
            category: 'Информация о пользователе',
            vars: [
                { name: '{userName}', description: 'Имя пользователя (нормализованное)' },
                { name: '{userEmail}', description: 'Email адрес пользователя' },
            ]
        },
        {
            category: 'Банковская информация',
            vars: [
                { name: '{bankAccount}', description: 'Криптоадрес или банковский счет пользователя' },
                { name: '{bank}', description: 'Название выбранного банка' },
            ]
        },
        {
            category: 'Финансовые данные',
            vars: [
                { name: '{currencySymbol}', description: 'Символ валюты (например: $, RM, MAD)' },
                { name: '{depositAmount}', description: 'Сумма депозита по текущему тарифу (отформатированная)' },
                { name: '{userWinning}', description: 'Сумма выигрыша пользователя (отформатированная)' },
                { name: '{userWinningPercent}', description: 'Процент выигрыша пользователя от депозита' },
                { name: '{userWinningPlusAdditional}', description: 'Выигрыш + дополнительная сумма' },
            ]
        },
        {
            category: 'Комиссии и налоги',
            vars: [
                { name: '{commissionAmount}', description: 'Сумма комиссии по текущему тарифу' },
                { name: '{commissionPercent}', description: 'Процент комиссии от выигрыша' },
                { name: '{verificationAmount}', description: 'Сумма верификации по текущему тарифу' },
                { name: '{additionalAmount}', description: 'Дополнительная сумма по текущему тарифу' },
                { name: '{taxPercent}', description: 'Процент налога по текущему тарифу' },
                { name: '{conversionFee}', description: 'Комиссия за конвертацию криптовалюты' },
                { name: '{bankFee}', description: 'Банковская комиссия по текущему тарифу' },
                { name: '{brokerFee}', description: 'Комиссия брокера по текущему тарифу' },
            ]
        },
        {
            category: 'Кастомные модалы',
            vars: (siteSettings?.modalTemplates || [])
                .filter(template => template.financeFieldName && template.actionKey && template.enabled !== false)
                .map(template => ({
                    name: `{${template.actionKey}Amount}`,
                    description: `${template.financeFieldName || template.name} - сумма из поля в Финансах для модала "${template.name}"`
                }))
        },
        {
            category: 'Кошельки - адреса',
            vars: [
                { name: '{mainWalletAddress}', description: 'Адрес основного кошелька' },
                { name: '{commissionWalletAddress}', description: 'Адрес кошелька для комиссии' },
                { name: '{verificationWalletAddress}', description: 'Адрес кошелька для верификации' },
                { name: '{taxWalletAddress}', description: 'Адрес кошелька для налога' },
                { name: '{cryptoConversionWalletAddress}', description: 'Адрес кошелька для конвертации крипты' },
                { name: '{bankConversionWalletAddress}', description: 'Адрес кошелька для конвертации банка' },
            ]
        },
        {
            category: 'Прочее',
            vars: [
                { name: '{currentTime}', description: 'Текущее время и дата (отформатированные для писем)' },
                { name: '{defaultCrypto}', description: 'Название дефолтной криптовалюты' },
                { name: '{verificationCode}', description: 'Код верификации (только для регистрационных писем)' },
            ]
        }
    ].filter(category => category.vars && category.vars.length > 0); // Убираем пустые категории

    return variables;
};

// Примерные значения переменных для живого предпросмотра
const SAMPLE_VALUES = {
    '{userName}': 'María González',
    '{userEmail}': 'maria.gonzalez@gmail.com',
    '{bankAccount}': '0000003100010000000001',
    '{bank}': 'Banco Santander',
    // ВАЖНО: суммы — голые числа, без символа валюты (реальная отправка подставляет
    // именно так, {currencySymbol} пишется в шаблоне отдельно). Проценты — уже со
    // знаком %, как при реальной отправке.
    '{currencySymbol}': '$',
    '{depositAmount}': '250.000',
    '{userWinning}': '1.250.000',
    '{userWinningPercent}': '500%',
    '{userWinningPlusAdditional}': '1.375.000',
    '{commissionAmount}': '62.500',
    '{commissionPercent}': '5%',
    '{verificationAmount}': '25.000',
    '{additionalAmount}': '125.000',
    '{taxPercent}': '3%',
    '{conversionFee}': '18.700',
    '{bankFee}': '12.400',
    '{brokerFee}': '9.800',
    '{mainWalletAddress}': 'TQrYceBqQkeAX2NDobfJP52dJfKcnzXk9F',
    '{commissionWalletAddress}': 'TWk3C7bQ9zq1XvGm4hR8sLuT6pYdEjNcAf',
    '{verificationWalletAddress}': 'TNv5D8mHs2xKpQr7YbLc4gWt9uZeFaJdMi',
    '{taxWalletAddress}': 'TPq6E9nJt3yLqRs8ZcMd5hXu1vAfGbKeNj',
    '{cryptoConversionWalletAddress}': 'TRs7F1oKu4zMrSt9AdNe6iYv2wBgHcLfOk',
    '{bankConversionWalletAddress}': 'TSt8G2pLv5AnStU1BeOf7jZw3xChIdMgPl',
    '{defaultCrypto}': 'USDT',
    '{verificationCode}': '482913'
};

// Помечает каждый открывающий тег его смещением в исходном коде (data-mt-idx).
// По этим меткам работает двусторонняя подсветка «код ↔ предпросмотр».
const tagHtmlWithOffsets = (src) =>
    String(src || '').replace(
        /<([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g,
        (match, tag, rest, offset) => `<${tag} data-mt-idx="${offset}"${rest}>`
    );

const applySampleValues = (content, siteSettings) => {
    const map = {
        ...SAMPLE_VALUES,
        '{currentTime}': new Date().toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    };
    (siteSettings?.modalTemplates || [])
        .filter(t => t.actionKey && t.financeFieldName && t.enabled !== false)
        .forEach(t => { map[`{${t.actionKey}Amount}`] = '50.000'; });
    return String(content || '').replace(/\{[a-zA-Z0-9_]+\}/g, (m) => (map[m] !== undefined ? map[m] : m));
};

// Выезжающая панель переменных с поиском
const VariablesPanel = ({ groups, onInsert, onClose }) => {
    const [query, setQuery] = useState('');
    const q = query.trim().toLowerCase();
    const filtered = groups
        .map(g => ({
            ...g,
            vars: g.vars.filter(v => !q || v.name.toLowerCase().includes(q) || v.description.toLowerCase().includes(q))
        }))
        .filter(g => g.vars.length > 0);
    const total = groups.reduce((sum, g) => sum + g.vars.length, 0);

    return (
        <aside className={s.varsPanel}>
            <div className={s.varsHead}>
                <span className={s.varsTitle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 18l6-6-6-6"></path>
                        <path d="M8 6l-6 6 6 6"></path>
                    </svg>
                    <span>Переменные</span>
                    <span className={s.varsCount}>{total}</span>
                </span>
                <button type="button" className={s.varsClose} onClick={onClose}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
            <p className={s.varsHint}>Клик — переменная вставится в позицию курсора. При отправке письма она заменится на реальное значение.</p>
            <div className={s.varsSearch}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Поиск переменной..."
                />
                {query && (
                    <button type="button" onClick={() => setQuery('')}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                )}
            </div>
            <div className={s.varsList}>
                {filtered.length === 0 ? (
                    <p className={s.varsEmpty}>Ничего не найдено</p>
                ) : (
                    filtered.map((category, catIndex) => (
                        <div key={catIndex} className={s.varsGroup}>
                            <div className={s.varsGroupTitle}>{category.category}</div>
                            {category.vars.map((variable, varIndex) => (
                                <button
                                    type="button"
                                    key={varIndex}
                                    className={s.varItem}
                                    onClick={() => onInsert(variable.name)}
                                >
                                    <code className={s.varName}>{variable.name}</code>
                                    <span className={s.varDesc}>{variable.description}</span>
                                </button>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </aside>
    );
};

const MailTab = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [activeTab, setActiveTab] = useState('templates'); // 'templates', 'settings', or 'sent'
    const segRef = useRef(null);
    const [segThumb, setSegThumb] = useState({ left: 0, width: 0, ready: false });
    const [segAnimate, setSegAnimate] = useState(false); // переход включаем после первого замера
    // Флаги и фильтры для вкладки «Шаблоны»
    const [mailConfigs, setMailConfigs] = useState([]); // [{ filename, name }]
    const [configFlags, setConfigFlags] = useState({}); // { filename: 'ar' }
    const [tplSearch, setTplSearch] = useState('');
    const [selectedCountries, setSelectedCountries] = useState([]); // ['ar', ...]
    const [selectedTplConfigs, setSelectedTplConfigs] = useState([]); // ['x.json', ...]
    const [showCountryDd, setShowCountryDd] = useState(false);
    const [showConfigDd, setShowConfigDd] = useState(false);
    const [configDdSearch, setConfigDdSearch] = useState('');

    // «Перетекающая» подложка активной вкладки: скользит к активной кнопке.
    // Первый замер — синхронно до отрисовки (useLayoutEffect) и без перехода,
    // чтобы кнопка была активна сразу, а не «дорисовывалась».
    useLayoutEffect(() => {
        const measure = () => {
            const wrap = segRef.current;
            if (!wrap) return;
            const btn = wrap.querySelector('[data-seg-active="true"]');
            if (!btn) return;
            setSegThumb({ left: btn.offsetLeft, width: btn.offsetWidth, ready: true });
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [activeTab, loading]);

    // Включаем анимацию скольжения только после первого позиционирования
    useEffect(() => {
        if (segThumb.ready && !segAnimate) {
            const id = requestAnimationFrame(() => setSegAnimate(true));
            return () => cancelAnimationFrame(id);
        }
    }, [segThumb.ready, segAnimate]);

    // Список конфигов + флаг основной страны каждого (для карточек и фильтров)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch('/api/files/configs');
                const data = await response.json();
                if (!data.success || cancelled) return;
                const configs = data.configs || [];
                setMailConfigs(configs);
                // Флаги — из общего кэша: мгновенно при повторных заходах,
                // одним обновлением (а не по одному) при первой загрузке
                setConfigFlags(getCachedConfigFlags());
                loadConfigFlags().then((flags) => { if (!cancelled) setConfigFlags(flags); });
            } catch (_) { /* нет конфигов — фильтры будут пустыми */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Закрытие фильтр-дропдаунов по клику вне (capture-фаза)
    useEffect(() => {
        const onOutside = (e) => {
            if (!e.target.closest(`.${s.filterWrapper}`)) {
                setShowCountryDd(false);
                setShowConfigDd(false);
                setConfigDdSearch('');
            }
        };
        document.addEventListener('mousedown', onOutside, true);
        return () => document.removeEventListener('mousedown', onOutside, true);
    }, []);

    const [siteSettings, setSiteSettings] = useState(null);
    const [mailgunSettings, setMailgunSettings] = useState({
        apiKey: '',
        domain: '',
        apiUrl: 'https://api.mailgun.net/v3',
        from: ''
    });
    const [resendSettings, setResendSettings] = useState({
        apiKey: '',
        from: 'noreply@cryptojapan.org'
    });
    const [emailProviderMode, setEmailProviderMode] = useState('auto');
    const [savingSettings, setSavingSettings] = useState(false);
    const [savingResendSettings, setSavingResendSettings] = useState(false);
    const [savingProviderMode, setSavingProviderMode] = useState(false);
    const [showMgKey, setShowMgKey] = useState(false);
    const [showRsKey, setShowRsKey] = useState(false);
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        actionKey: '',
        subject: '',
        html: '',
        content: '',
        type: 'html', // 'html' or 'text'
        useOnRegistration: false,
        useOnSuccess: false,
        allowedConfigs: [],
        fromDomain: ''
    });

    useEffect(() => {
        loadTemplates();
        loadMailgunSettings();
        loadResendSettings();
        loadEmailProviderMode();
        loadSiteSettings();
    }, []);

    const loadSiteSettings = async () => {
        try {
            // Загружаем текущий конфиг чтобы получить modalTemplates
            const configs = await fetch('/api/files/configs').then(r => r.json()).then(d => d.configs || []);
            const activeConfigFile = localStorage.getItem('currentConfigFile') || configs[0]?.filename;
            if (activeConfigFile) {
                const configData = await fetch(`/api/files/configs/${encodeURIComponent(activeConfigFile)}`).then(r => r.json());
                setSiteSettings(configData);
            }
        } catch (e) {
            console.error('Ошибка загрузки конфига для переменных:', e);
        }
    };

    const loadMailgunSettings = async () => {
        try {
            const response = await fetch('/api/config/mailgun', {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success && data.settings) {
                setMailgunSettings({
                    apiKey: data.settings.apiKey || '',
                    domain: data.settings.domain || '',
                    apiUrl: data.settings.apiUrl || 'https://api.mailgun.net/v3',
                    from: data.settings.from || ''
                });
            }
        } catch (err) {
            console.error('Ошибка загрузки настроек Mailgun:', err);
        }
    };

    const saveMailgunSettings = async () => {
        if (!mailgunSettings.apiKey || !mailgunSettings.domain) {
            notifyWarning('Заполните API ключ и домен');
            return;
        }

        try {
            setSavingSettings(true);
            const response = await fetch('/api/config/mailgun', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({
                    settings: mailgunSettings
                })
            });

            // Проверяем тип ответа
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Сервер вернул не JSON:', text.substring(0, 200));
                notifyError('Ошибка: сервер вернул неверный формат ответа. Убедитесь, что сервер запущен и перезапущен после изменений.');
                return;
            }

            const data = await response.json();
            if (data.success) {
                notifySuccess('Настройки Mailgun сохранены');
            } else {
                notifyError('Ошибка сохранения настроек: ' + (data.error || ''));
            }
        } catch (err) {
            console.error('Ошибка сохранения настроек:', err);
            notifyError('Ошибка сохранения настроек: ' + err.message + '\n\nУбедитесь, что сервер запущен на порту 3002.');
        } finally {
            setSavingSettings(false);
        }
    };

    const loadResendSettings = async () => {
        try {
            const response = await fetch('/api/config/resend', {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success && data.settings) {
                setResendSettings({
                    apiKey: data.settings.apiKey || '',
                    from: data.settings.from || 'noreply@cryptojapan.org'
                });
            }
        } catch (err) {
            console.error('Ошибка загрузки настроек Resend:', err);
        }
    };

    const saveResendSettings = async () => {
        if (!resendSettings.apiKey) {
            notifyWarning('Заполните API ключ Resend');
            return;
        }
        if (!resendSettings.from) {
            notifyWarning('Укажите From Email для Resend');
            return;
        }

        try {
            setSavingResendSettings(true);
            const response = await fetch('/api/config/resend', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({
                    settings: resendSettings
                })
            });
            const data = await response.json();
            if (data.success) {
                notifySuccess('Настройки Resend сохранены');
            } else {
                notifyError('Ошибка сохранения настроек Resend: ' + (data.error || ''));
            }
        } catch (err) {
            console.error('Ошибка сохранения настроек Resend:', err);
            notifyError('Ошибка сохранения настроек Resend: ' + err.message);
        } finally {
            setSavingResendSettings(false);
        }
    };

    const loadEmailProviderMode = async () => {
        try {
            const response = await fetch('/api/config/email/provider', {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success && ['mailgun', 'resend', 'auto'].includes(data.mode)) {
                setEmailProviderMode(data.mode);
            }
        } catch (err) {
            console.error('Ошибка загрузки режима отправки email:', err);
        }
    };

    const saveEmailProviderMode = async () => {
        try {
            setSavingProviderMode(true);
            const response = await fetch('/api/config/email/provider', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ mode: emailProviderMode })
            });
            const data = await response.json();
            if (data.success) {
                notifySuccess('Режим отправки email сохранен');
            } else {
                notifyError('Ошибка сохранения режима: ' + (data.error || ''));
            }
        } catch (err) {
            console.error('Ошибка сохранения режима отправки email:', err);
            notifyError('Ошибка сохранения режима: ' + err.message);
        } finally {
            setSavingProviderMode(false);
        }
    };

    const loadTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/api/config/templates/');
            const data = await response.json();
            if (data.success) {
                setTemplates(data.templates || []);
            } else {
                setError('Не удалось загрузить шаблоны');
            }
        } catch (err) {
            console.error('Ошибка загрузки шаблонов:', err);
            setError('Не удалось загрузить шаблоны');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableConfigs = async () => {
        try {
            const response = await fetch('/api/files/configs');
            const data = await response.json();
            if (data.success) {
                return data.configs || [];
            }
        } catch (err) {
            console.error('Ошибка загрузки конфигов:', err);
        }
        return [];
    };

    const handleCreateNew = () => {
        setEditingTemplate(null);
        setFormData({
            id: `template_${Date.now()}`,
            name: '',
            actionKey: '',
            subject: '',
            html: '',
            content: '',
            type: 'html',
            useOnRegistration: false,
            useOnSuccess: false,
            allowedConfigs: [],
            fromDomain: ''
        });
        setShowModal(true);
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        setFormData({
            id: template.id || `template_${Date.now()}`,
            name: template.name || '',
            actionKey: template.actionKey || '',
            subject: template.subject || '',
            html: template.html || '',
            content: template.content || '',
            type: template.type || 'html',
            useOnRegistration: template.useOnRegistration || false,
            useOnSuccess: template.useOnSuccess || false,
            allowedConfigs: template.allowedConfigs || [],
            fromDomain: template.fromDomain || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (templateId) => {
        if (!(await confirmDialog({
            title: 'Удалить шаблон письма?',
            message: 'Шаблон будет удалён с сервера навсегда. Письма по нему отправлять больше не получится.',
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Безвозвратно удалит шаблон письма',
            cancelHint: 'Закроет окно, шаблон останется'
        }))) {
            return;
        }

        try {
            const response = await fetch(`/api/config/templates/${templateId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                await loadTemplates();
            } else {
                notifyError('Ошибка удаления шаблона: ' + (data.error || ''));
            }
        } catch (err) {
            console.error('Ошибка удаления шаблона:', err);
            notifyError('Ошибка удаления шаблона: ' + err.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.id || !formData.subject) {
            notifyWarning('Заполните все обязательные поля (ID, Subject)');
            return;
        }

        // Action Key обязателен только если шаблон не для регистрации/успеха
        if (!formData.actionKey && !formData.useOnRegistration && !formData.useOnSuccess) {
            notifyWarning('Заполните Action Key или отметьте "Использовать при регистрации" / "Использовать при успехе"');
            return;
        }

        try {
            const templateToSave = {
                ...formData,
                updatedAt: new Date().toISOString()
            };

            if (!editingTemplate) {
                templateToSave.createdAt = new Date().toISOString();
            }

            const response = await fetch('/api/config/templates/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(templateToSave)
            });

            const data = await response.json();
            if (data.success) {
                await loadTemplates();
                setShowModal(false);
                setEditingTemplate(null);
            } else {
                notifyError('Ошибка сохранения шаблона: ' + (data.error || ''));
            }
        } catch (err) {
            console.error('Ошибка сохранения шаблона:', err);
            notifyError('Ошибка сохранения шаблона: ' + err.message);
        }
    };

    const handleConfigToggle = (configFilename) => {
        setFormData(prev => {
            const currentConfigs = prev.allowedConfigs || [];
            const newConfigs = currentConfigs.includes(configFilename)
                ? currentConfigs.filter(c => c !== configFilename)
                : [...currentConfigs, configFilename];
            return { ...prev, allowedConfigs: newConfigs };
        });
    };

    const actionKeys = [
        { value: 'commission', label: 'Комиссия' },
        { value: 'verification', label: 'Верификация' },
        { value: 'success', label: 'Успех' },
        { value: 'tax', label: 'Налог' },
        { value: 'cryptoConversion', label: 'Конверт крипта' },
        { value: 'bankConversion', label: 'Конверт банк' },
        { value: 'code', label: 'Код' }
    ];

    // Режимы отправки: карточки-опции вместо дропдауна
    const modeOptions = [
        { value: 'auto', title: 'Авто', desc: 'Сначала Mailgun, при ошибке — Resend', flow: 'Mailgun → Resend' },
        { value: 'mailgun', title: 'Только Mailgun', desc: 'Все письма уходят через Mailgun', flow: 'Mailgun' },
        { value: 'resend', title: 'Только Resend', desc: 'Все письма уходят через Resend', flow: 'Resend' }
    ];

    // Роль/статус провайдера в текущем режиме (для пилюли в карточке)
    const providerStatus = (provider) => {
        if (emailProviderMode === 'auto') {
            return provider === 'mailgun'
                ? { role: 'Основной провайдер', pill: 'Основной', tone: 'good' }
                : { role: 'Резервный провайдер', pill: 'Резерв', tone: 'info' };
        }
        if (emailProviderMode === provider) return { role: 'Активный провайдер', pill: 'Активен', tone: 'good' };
        return { role: 'Не используется', pill: 'Отключён', tone: 'muted' };
    };

    // Флаги конфигов, к которым относится шаблон (пусто = все конфиги)
    const templateFlags = (template) => {
        const cfgs = template.allowedConfigs || [];
        const list = cfgs.length ? cfgs : mailConfigs.map((c) => c.filename || c.name || c);
        const seen = new Set();
        const flags = [];
        list.forEach((fn) => {
            const code = configFlags[fn] || 'unknown';
            if (!seen.has(code)) { seen.add(code); flags.push({ fn, code }); }
        });
        return flags;
    };

    // Уникальные страны среди всех конфигов — для фильтра по странам
    const countryList = useMemo(() => {
        const set = new Set();
        Object.values(configFlags).forEach((code) => { if (code) set.add(code); });
        return Array.from(set).sort();
    }, [configFlags]);

    const filteredTemplates = useMemo(() => {
        const q = tplSearch.trim().toLowerCase();
        return templates.filter((t) => {
            if (q) {
                const hay = `${t.name || ''} ${t.subject || ''} ${t.id || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            const cfgs = t.allowedConfigs || [];
            // пустой список = доступен во всех конфигах → проходит любой фильтр
            const isAll = cfgs.length === 0;
            if (selectedTplConfigs.length > 0) {
                if (!isAll && !cfgs.some((fn) => selectedTplConfigs.includes(fn))) return false;
            }
            if (selectedCountries.length > 0) {
                if (!isAll) {
                    const codes = cfgs.map((fn) => configFlags[fn]).filter(Boolean);
                    if (!codes.some((c) => selectedCountries.includes(c))) return false;
                }
            }
            return true;
        });
    }, [templates, tplSearch, selectedTplConfigs, selectedCountries, configFlags]);

    const hasTplFilters = tplSearch.trim() || selectedCountries.length > 0 || selectedTplConfigs.length > 0;

    const clearTplFilters = () => {
        setTplSearch('');
        setSelectedCountries([]);
        setSelectedTplConfigs([]);
        setConfigDdSearch('');
    };

    if (loading) {
        return (
            <div className={s.mailTab}>
                <div className={s.loading}>Загрузка шаблонов...</div>
            </div>
        );
    }

    const mailTabs = [
        { id: 'templates', label: 'Шаблоны', icon: (<><rect x="4" y="4" width="16" height="16" rx="2"></rect><line x1="8" y1="8" x2="16" y2="8"></line><line x1="8" y1="12" x2="16" y2="12"></line></>) },
        { id: 'sent', label: 'Отправленные', icon: (<><path d="M22 2 11 13"></path><path d="M22 2 15 22 11 13 2 9 22 2z"></path></>) },
        { id: 'settings', label: 'Настройки Email', icon: (<><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></>) }
    ];

    return (
        <div className={s.mailTab}>
            <section className={s.panel}>
                <header className={s.panelHead}>
                    <div className={s.panelHeadMain}>
                        <span className={s.panelIcon}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                <path d="m3 7 9 6 9-6"></path>
                            </svg>
                        </span>
                        <div className={s.panelHeadText}>
                            <h2 className={s.panelTitle}>Управление почтой</h2>
                            <p className={s.panelSub}>Шаблоны, история отправок и настройки провайдеров в одном разделе</p>
                        </div>
                    </div>
                    <div className={s.panelHeadSide}>
                        <span className={s.statChip}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                                <line x1="4" y1="9" x2="20" y2="9"></line>
                            </svg>
                            <span>Шаблонов: <strong>{activeTab === 'templates' && hasTplFilters ? `${filteredTemplates.length} / ${templates.length}` : templates.length}</strong></span>
                        </span>
                        {activeTab === 'templates' && (
                            <button className={s.addButton} onClick={handleCreateNew}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                <span>Создать шаблон</span>
                            </button>
                        )}
                    </div>
                </header>

                <div ref={segRef} className={s.segmented} role="tablist">
                    <span
                        className={s.segThumb}
                        aria-hidden="true"
                        style={{
                            transform: `translateX(${segThumb.left}px)`,
                            width: `${segThumb.width}px`,
                            opacity: segThumb.ready ? 1 : 0,
                            transition: segAnimate ? undefined : 'none'
                        }}
                    />
                    {mailTabs.map((t) => (
                        <button
                            key={t.id}
                            role="tab"
                            data-seg-active={activeTab === t.id}
                            className={`${s.segTab} ${activeTab === t.id ? s.segTabActive : ''}`}
                            onClick={() => setActiveTab(t.id)}
                        >
                            <svg className={s.segIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{t.icon}</svg>
                            <span>{t.label}</span>
                        </button>
                    ))}
                </div>

                <div className={s.panelBody}>
            {activeTab === 'sent' ? (
                <SentEmailsTab />
            ) : activeTab === 'settings' ? (
                <div className={s.settingsSection}>
                    {/* ── Режим отправки: карточки-опции */}
                    <div className={`${s.settingsCard} ${s.settingsCardPrimary}`}>
                        <h3 className={s.settingsTitle}>
                            <span className={s.titleIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 7h-9"></path>
                                    <path d="M14 17H5"></path>
                                    <circle cx="17" cy="17" r="3"></circle>
                                    <circle cx="7" cy="7" r="3"></circle>
                                </svg>
                            </span>
                            <span>Режим отправки писем</span>
                        </h3>
                        <p className={s.settingsDescription}>
                            Определяет, какой сервис доставляет письма. В режиме «Авто» сервер сначала пробует Mailgun и при сбое переключается на Resend.
                        </p>

                        <div className={s.modeGrid} role="radiogroup" aria-label="Режим отправки">
                            {modeOptions.map((m) => {
                                const active = emailProviderMode === m.value;
                                return (
                                    <button
                                        key={m.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        className={`${s.modeCard} ${active ? s.modeCardActive : ''}`}
                                        onClick={() => setEmailProviderMode(m.value)}
                                    >
                                        <span className={s.modeRadio} aria-hidden="true" />
                                        <span className={s.modeBody}>
                                            <span className={s.modeTitle}>{m.title}</span>
                                            <span className={s.modeDesc}>{m.desc}</span>
                                        </span>
                                        <span className={s.modeFlow}>{m.flow}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className={s.settingsActions}>
                            <button
                                className={s.saveButton}
                                onClick={saveEmailProviderMode}
                                disabled={savingProviderMode}
                            >
                                <svg className={s.buttonIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                    <polyline points="7 3 7 8 15 8"></polyline>
                                </svg>
                                <span>{savingProviderMode ? 'Сохранение...' : 'Сохранить режим'}</span>
                            </button>
                        </div>
                    </div>

                    <div className={s.settingsGrid}>
                    {/* ── Mailgun */}
                    <div className={s.settingsCard}>
                        {(() => { const st = providerStatus('mailgun'); return (
                        <div className={s.providerHead}>
                            <span className={s.providerLogo} data-provider="mailgun">MG</span>
                            <div className={s.providerHeadText}>
                                <h3 className={s.providerName}>Mailgun</h3>
                                <span className={s.providerRole}>{st.role}</span>
                            </div>
                            <span className={s.providerPill} data-tone={st.tone}>
                                <span className={s.providerDot} />
                                {st.pill}
                            </span>
                        </div>
                        ); })()}

                        <div className={s.formGroup}>
                            <label className={s.label}>API Key <span className={s.required}>*</span></label>
                            <div className={s.secretField}>
                                <input
                                    type={showMgKey ? 'text' : 'password'}
                                    className={`${s.input} ${s.inputMono}`}
                                    value={mailgunSettings.apiKey}
                                    onChange={(e) => setMailgunSettings({ ...mailgunSettings, apiKey: e.target.value })}
                                    placeholder="key-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                />
                                <button type="button" className={s.secretToggle} onClick={() => setShowMgKey(!showMgKey)} title={showMgKey ? 'Скрыть' : 'Показать'}>
                                    {showMgKey ? (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                    ) : (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className={s.formGroup}>
                            <label className={s.label}>Domain <span className={s.required}>*</span></label>
                            <input
                                type="text"
                                className={s.input}
                                value={mailgunSettings.domain}
                                onChange={(e) => setMailgunSettings({ ...mailgunSettings, domain: e.target.value })}
                                placeholder="mg.example.com"
                            />
                        </div>

                        <div className={s.formRow}>
                            <div className={s.formGroup}>
                                <label className={s.label}>API URL</label>
                                <input
                                    type="text"
                                    className={s.input}
                                    value={mailgunSettings.apiUrl}
                                    onChange={(e) => setMailgunSettings({ ...mailgunSettings, apiUrl: e.target.value })}
                                    placeholder="https://api.mailgun.net/v3"
                                />
                                <div className={s.helpText}>Для EU-региона: https://api.eu.mailgun.net/v3</div>
                            </div>
                            <div className={s.formGroup}>
                                <label className={s.label}>From Email</label>
                                <input
                                    type="email"
                                    className={s.input}
                                    value={mailgunSettings.from}
                                    onChange={(e) => setMailgunSettings({ ...mailgunSettings, from: e.target.value })}
                                    placeholder="noreply@yourdomain.com"
                                />
                                <div className={s.helpText}>Если пусто — noreply@domain</div>
                            </div>
                        </div>

                        <div className={s.settingsActions}>
                            <button className={s.saveButton} onClick={saveMailgunSettings} disabled={savingSettings}>
                                <svg className={s.buttonIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                    <polyline points="7 3 7 8 15 8"></polyline>
                                </svg>
                                <span>{savingSettings ? 'Сохранение...' : 'Сохранить Mailgun'}</span>
                            </button>
                        </div>
                    </div>

                    {/* ── Resend */}
                    <div className={s.settingsCard}>
                        {(() => { const st = providerStatus('resend'); return (
                        <div className={s.providerHead}>
                            <span className={s.providerLogo} data-provider="resend">RS</span>
                            <div className={s.providerHeadText}>
                                <h3 className={s.providerName}>Resend</h3>
                                <span className={s.providerRole}>{st.role}</span>
                            </div>
                            <span className={s.providerPill} data-tone={st.tone}>
                                <span className={s.providerDot} />
                                {st.pill}
                            </span>
                        </div>
                        ); })()}

                        <div className={s.formGroup}>
                            <label className={s.label}>API Key <span className={s.required}>*</span></label>
                            <div className={s.secretField}>
                                <input
                                    type={showRsKey ? 'text' : 'password'}
                                    className={`${s.input} ${s.inputMono}`}
                                    value={resendSettings.apiKey}
                                    onChange={(e) => setResendSettings({ ...resendSettings, apiKey: e.target.value })}
                                    placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                />
                                <button type="button" className={s.secretToggle} onClick={() => setShowRsKey(!showRsKey)} title={showRsKey ? 'Скрыть' : 'Показать'}>
                                    {showRsKey ? (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                    ) : (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className={s.formGroup}>
                            <label className={s.label}>From Email <span className={s.required}>*</span></label>
                            <input
                                type="email"
                                className={s.input}
                                value={resendSettings.from}
                                onChange={(e) => setResendSettings({ ...resendSettings, from: e.target.value })}
                                placeholder="noreply@cryptojapan.org"
                            />
                            <div className={s.helpText}>Должен быть с верифицированного домена в Resend.</div>
                        </div>

                        <div className={s.settingsActions}>
                            <button className={s.saveButton} onClick={saveResendSettings} disabled={savingResendSettings}>
                                <svg className={s.buttonIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                    <polyline points="7 3 7 8 15 8"></polyline>
                                </svg>
                                <span>{savingResendSettings ? 'Сохранение...' : 'Сохранить Resend'}</span>
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            ) : (
                <>
                    {error && <div className={s.error}>{error}</div>}

                    {templates.length === 0 ? (
                        <div className={s.empty}>
                            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={s.emptyIcon}>
                                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                <path d="m22 7-10 5L2 7"></path>
                            </svg>
                            <p>Шаблоны не найдены. Создайте первый шаблон.</p>
                            <button className={s.emptyAddButton} onClick={handleCreateNew}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                <span>Создать первый шаблон</span>
                            </button>
                        </div>
                    ) : (
                        <>
                        <div className={s.controls}>
                            <div className={s.searchContainer}>
                                <span className={s.searchIcon}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                </span>
                                <input
                                    className={s.searchInput}
                                    type="text"
                                    placeholder="Поиск по названию, теме, ID…"
                                    value={tplSearch}
                                    onChange={(e) => setTplSearch(e.target.value)}
                                />
                                {tplSearch && (
                                    <button className={s.searchClear} onClick={() => setTplSearch('')} title="Очистить">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {/* Фильтр по странам */}
                            <div className={s.filterWrapper}>
                                <button
                                    type="button"
                                    className={`${s.filterButton} ${selectedCountries.length > 0 ? s.filterActive : ''}`}
                                    onClick={() => { setShowCountryDd(!showCountryDd); setShowConfigDd(false); }}
                                >
                                    <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="2" y1="12" x2="22" y2="12"></line>
                                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                                    </svg>
                                    <span>{selectedCountries.length > 0 ? `Страны: ${selectedCountries.length}` : 'Все страны'}</span>
                                    <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points={showCountryDd ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                                    </svg>
                                </button>
                                {showCountryDd && (
                                    <div className={s.filterMenu}>
                                        <div className={s.filterMenuHead}>
                                            <span>Страны</span>
                                            {selectedCountries.length > 0 && (
                                                <button className={s.clearAllButton} onClick={() => setSelectedCountries([])}>Сбросить</button>
                                            )}
                                        </div>
                                        <div className={s.checkboxList}>
                                            {countryList.length === 0 ? (
                                                <p className={s.noResults}>Нет данных о странах</p>
                                            ) : countryList.map((code) => {
                                                const checked = selectedCountries.includes(code);
                                                return (
                                                    <label key={code} className={s.checkboxItem}>
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => setSelectedCountries((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code])}
                                                        />
                                                        <span className={s.flagOption}>
                                                            <FlagIcon code={code} className={s.flagMini} alt="" />
                                                            <span>{code.toUpperCase()}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Фильтр по конфигам */}
                            <div className={s.filterWrapper}>
                                <button
                                    type="button"
                                    className={`${s.filterButton} ${selectedTplConfigs.length > 0 ? s.filterActive : ''}`}
                                    onClick={() => { setShowConfigDd(!showConfigDd); setShowCountryDd(false); }}
                                >
                                    <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    <span>{selectedTplConfigs.length > 0 ? `Конфиги: ${selectedTplConfigs.length}` : 'Все конфиги'}</span>
                                    <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points={showConfigDd ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                                    </svg>
                                </button>
                                {showConfigDd && (
                                    <div className={s.filterMenu}>
                                        <div className={s.filterMenuHead}>
                                            <span>Конфиги</span>
                                            {selectedTplConfigs.length > 0 && (
                                                <button className={s.clearAllButton} onClick={() => setSelectedTplConfigs([])}>Сбросить</button>
                                            )}
                                        </div>
                                        <div className={s.configsSearch}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="11" cy="11" r="8"></circle>
                                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                            </svg>
                                            <input
                                                type="text"
                                                placeholder="Найти конфиг…"
                                                value={configDdSearch}
                                                onChange={(e) => setConfigDdSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className={s.checkboxList}>
                                            {(() => {
                                                const q = configDdSearch.trim().toLowerCase();
                                                const list = mailConfigs.filter((c) => {
                                                    const name = c.name || (c.filename || c).replace('.json', '');
                                                    return !q || name.toLowerCase().includes(q);
                                                });
                                                if (list.length === 0) return <p className={s.noResults}>Ничего не найдено</p>;
                                                return list.map((config) => {
                                                    const filename = config.filename || config.name || config;
                                                    const name = config.name || filename.replace('.json', '');
                                                    const checked = selectedTplConfigs.includes(filename);
                                                    return (
                                                        <label key={filename} className={s.checkboxItem}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => setSelectedTplConfigs((prev) => prev.includes(filename) ? prev.filter((f) => f !== filename) : [...prev, filename])}
                                                            />
                                                            <span className={s.flagOption}>
                                                                <FlagIcon code={configFlags[filename] || 'unknown'} className={s.flagMini} alt="" />
                                                                <span>{name}</span>
                                                            </span>
                                                        </label>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {hasTplFilters && (
                                <button className={s.resetFilters} onClick={clearTplFilters}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                    <span>Сбросить</span>
                                </button>
                            )}
                        </div>

                        {filteredTemplates.length === 0 ? (
                            <div className={s.empty}>
                                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={s.emptyIcon}>
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                                <p>По заданным фильтрам шаблоны не найдены.</p>
                                <button className={s.emptyAddButton} onClick={clearTplFilters}>
                                    <span>Сбросить фильтры</span>
                                </button>
                            </div>
                        ) : (
                        <div className={s.templatesList}>
                            {filteredTemplates.map((template) => {
                                const isHtml = template.type === 'html';
                                const purpose = template.useOnRegistration
                                    ? 'Регистрация'
                                    : template.useOnSuccess
                                        ? 'Успех'
                                        : (actionKeys.find(k => k.value === template.actionKey)?.label || template.actionKey || 'Быстрое действие');
                                const cfgCount = (template.allowedConfigs || []).length;
                                const tplFlags = templateFlags(template);
                                return (
                                <div key={template.id} className={s.templateCard}>
                                    <div className={s.cardHeader}>
                                        <span className={s.cardAvatar}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                                <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                                <path d="m3 7 9 6 9-6"></path>
                                            </svg>
                                        </span>
                                        <div className={s.headMain}>
                                            <h3 className={s.templateName} title={template.name || template.id}>{template.name || template.id}</h3>
                                            <div className={s.headChips}>
                                                <span className={`${s.typeChip} ${isHtml ? s.typeHtml : s.typeText}`}>{isHtml ? 'HTML' : 'TEXT'}</span>
                                                <span className={s.purposeChip}>{purpose}</span>
                                            </div>
                                        </div>
                                        <div className={s.headActions}>
                                            <button className={s.iconEdit} onClick={() => handleEdit(template)} title="Редактировать">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                </svg>
                                            </button>
                                            <button className={s.iconDelete} onClick={() => handleDelete(template.id)} title="Удалить">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div className={s.cardBody}>
                                        <div className={s.factRow}>
                                            <span className={s.factLabel}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                                    <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                                    <path d="m3 7 9 6 9-6"></path>
                                                </svg>
                                                <span>Тема</span>
                                            </span>
                                            <span className={s.factValue} title={template.subject || ''}>{template.subject || '—'}</span>
                                        </div>
                                        <div className={s.metaStrip}>
                                            <span className={s.metaChip} title="Action Key">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 2l-2 2m-7.61 7.61a5 5 0 1 1 7.07 7.07l-4.24 4.24a5 5 0 0 1-7.07-7.07l1.41-1.41"></path>
                                                </svg>
                                                <span>{template.actionKey || '—'}</span>
                                            </span>
                                            {template.fromDomain && (
                                                <span className={s.metaChip} title="Отправитель письма">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="4"></circle>
                                                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"></path>
                                                    </svg>
                                                    <span>{template.fromDomain}</span>
                                                </span>
                                            )}
                                            <span className={s.metaChip} title={cfgCount > 0 ? 'Доступные конфиги' : 'Доступен во всех конфигах'}>
                                                {tplFlags.length > 0 && (
                                                    <span className={s.flagStack}>
                                                        {tplFlags.slice(0, 4).map(({ fn, code }) => (
                                                            <FlagIcon key={fn} code={code} className={s.flagMini} alt="" />
                                                        ))}
                                                    </span>
                                                )}
                                                <span>{cfgCount > 0 ? pluralConfigs(cfgCount) : 'Все конфиги'}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        )}
                        </>
                    )}

                    {showModal && (
                        <TemplateModal
                            formData={formData}
                            setFormData={setFormData}
                            onSubmit={handleSubmit}
                            onClose={() => {
                                setShowModal(false);
                                setEditingTemplate(null);
                            }}
                            actionKeys={actionKeys}
                            onConfigToggle={handleConfigToggle}
                            loadAvailableConfigs={loadAvailableConfigs}
                            siteSettings={siteSettings}
                        />
                    )}
                </>
            )}
                </div>
            </section>
        </div>
    );
};

const TemplateModal = ({ formData, setFormData, onSubmit, onClose, actionKeys, onConfigToggle, loadAvailableConfigs, siteSettings }) => {
    const [availableConfigs, setAvailableConfigs] = useState([]);
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [isDarkEditor, setIsDarkEditor] = useState(false);
    const [showActionKeyDd, setShowActionKeyDd] = useState(false);
    const [showSenderDd, setShowSenderDd] = useState(false);
    // Доступ по конфигам: поиск + группировка по странам (как в конфиг-пикере админки)
    const [cfgCachedFlags, setCfgCachedFlags] = useState(getCachedConfigFlags);
    const [cfgQuery, setCfgQuery] = useState('');
    const [cfgExpanded, setCfgExpanded] = useState(() => new Set());
    const cfgExpandedInitRef = useRef(false);
    const [viewMode, setViewMode] = useState('split'); // 'code' | 'split' | 'preview'
    const [showVars, setShowVars] = useState(false);
    const [sampleData, setSampleData] = useState(true);
    const htmlEditorViewRef = useRef(null);
    const textEditorViewRef = useRef(null);
    const previewFrameRef = useRef(null);
    const lastCursorPosRef = useRef(null);

    useEffect(() => {
        const onOutside = (e) => {
            if (!e.target.closest(`.${s.ddWrapper}`)) {
                setShowActionKeyDd(false);
                setShowSenderDd(false);
            }
        };
        // capture-фаза: stopPropagation на контенте модала не мешает закрытию
        document.addEventListener('mousedown', onOutside, true);
        return () => document.removeEventListener('mousedown', onOutside, true);
    }, []);

    useEffect(() => {
        const loadConfigs = async () => {
            setLoadingConfigs(true);
            const configs = await loadAvailableConfigs();
            setAvailableConfigs(configs);
            setLoadingConfigs(false);
        };
        loadConfigs();
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const syncTheme = () => {
            // На странице настроек тема управляется через data-nova-mode
            const novaMode = root.getAttribute('data-nova-mode');
            if (novaMode) {
                setIsDarkEditor(novaMode === 'dark');
                return;
            }
            const theme = root.getAttribute('data-theme') || 'light';
            setIsDarkEditor(theme === 'dark' || theme === 'binance' || theme === 'solana' || theme === 'liquid-glass');
        };
        syncTheme();
        const observer = new MutationObserver(syncTheme);
        observer.observe(root, { attributes: true, attributeFilter: ['data-theme', 'data-nova-mode'] });
        return () => observer.disconnect();
    }, []);

    const insertVariable = (variableName) => {
        const activeView = formData.type === 'html' ? htmlEditorViewRef.current : textEditorViewRef.current;
        if (activeView && !activeView.destroyed) {
            const { from, to } = activeView.state.selection.main;
            activeView.dispatch({
                changes: { from, to, insert: variableName },
                selection: { anchor: from + variableName.length }
            });
            activeView.focus();
            return;
        }
        const currentValue = formData.type === 'html' ? formData.html : formData.content;
        const nextValue = `${currentValue || ''}${variableName}`;
        if (formData.type === 'html') {
            setFormData({ ...formData, html: nextValue });
        } else {
            setFormData({ ...formData, content: nextValue });
        }
    };

    // Флаги: приоритет — флаг из списка конфигов (сервер отдаёт сразу), поверх — кэш
    useEffect(() => {
        loadConfigFlags().then(setCfgCachedFlags);
    }, [availableConfigs]);

    const cfgFlags = useMemo(() => {
        const m = { ...cfgCachedFlags };
        (availableConfigs || []).forEach((c) => {
            const fn = c.filename || c;
            if (c && c.flag) m[fn] = c.flag;
        });
        return m;
    }, [availableConfigs, cfgCachedFlags]);

    const cfgQ = cfgQuery.trim().toLowerCase();

    // Группы «страна → конфиги», отсортированные по названию страны; «Без страны» — в конце
    const cfgGroups = useMemo(() => {
        const byCode = new Map();
        (availableConfigs || []).forEach((config) => {
            const filename = config.filename || config;
            const name = config.name || String(filename).replace('.json', '');
            const code = cfgFlags[filename] || 'unknown';
            if (!byCode.has(code)) {
                byCode.set(code, {
                    code,
                    label: code === 'unknown' ? 'Без страны' : getFlagInfo(code).name,
                    items: []
                });
            }
            byCode.get(code).items.push({ filename, name });
        });
        const list = Array.from(byCode.values());
        list.forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name, 'ru')));
        list.sort((a, b) => {
            if (a.code === 'unknown') return 1;
            if (b.code === 'unknown') return -1;
            return a.label.localeCompare(b.label, 'ru');
        });
        if (!cfgQ) return list;
        // Поиск по имени конфига и названию страны; пустые группы прячем
        return list
            .map((g) => ({
                ...g,
                items: g.label.toLowerCase().includes(cfgQ)
                    ? g.items
                    : g.items.filter((it) => it.name.toLowerCase().includes(cfgQ))
            }))
            .filter((g) => g.items.length > 0);
    }, [availableConfigs, cfgFlags, cfgQ]);

    // При первой загрузке списка раскрываем группы, где уже есть выбранные конфиги
    useEffect(() => {
        if (cfgExpandedInitRef.current || !(availableConfigs || []).length) return;
        cfgExpandedInitRef.current = true;
        const selected = new Set(formData.allowedConfigs || []);
        const open = new Set();
        (availableConfigs || []).forEach((c) => {
            const fn = c.filename || c;
            if (selected.has(fn)) open.add(cfgFlags[fn] || 'unknown');
        });
        setCfgExpanded(open);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableConfigs]);

    const toggleCfgGroup = (code) => {
        setCfgExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    // Домены отправителя, верифицированные в Resend
    const senderOptions = [
        { value: '', label: 'noreply@cryptojapan.org', note: 'Основной — по умолчанию' },
        { value: 'cryptokyo.trade', label: 'noreply@cryptokyo.trade', note: 'Альтернативный домен' }
    ];
    const currentSender = senderOptions.find(o => o.value === (formData.fromDomain || '')) || senderOptions[0];

    const isHtml = formData.type === 'html';
    const hasTrigger = Boolean(formData.actionKey || formData.useOnRegistration || formData.useOnSuccess);
    const canSave = Boolean(formData.id && formData.subject && hasTrigger);
    const isCreate = formData.id && formData.id.startsWith('template_') && !formData.name;

    const variableGroups = useMemo(() => getVariableGroups(siteSettings), [siteSettings]);

    const rawContent = isHtml ? (formData.html || '') : (formData.content || '');
    const previewContent = sampleData ? applySampleValues(rawContent, siteSettings) : rawContent;
    const previewSubject = sampleData ? applySampleValues(formData.subject || '', siteSettings) : (formData.subject || '');
    const previewDoc = useMemo(() => {
        if (!isHtml) return '';
        // Сначала метим теги смещениями по исходному коду, потом подставляем примеры —
        // так метки всегда указывают на реальные позиции в редакторе
        const tagged = tagHtmlWithOffsets(formData.html || '');
        const content = sampleData ? applySampleValues(tagged, siteSettings) : tagged;
        return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:18px;background:#ffffff;color:#111827;font-family:-apple-system,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}img{max-width:100%;height:auto}table{border-collapse:collapse;max-width:100%}a{color:#2563eb}[data-mt-hover]{outline:1.5px dashed rgba(123,127,242,0.65);outline-offset:2px;cursor:pointer}[data-mt-hl]{outline:2px solid #7B7FF2;outline-offset:2px;box-shadow:0 0 0 5px rgba(123,127,242,0.16);border-radius:2px}</style></head><body>${content}</body></html>`;
    }, [isHtml, formData.html, sampleData, siteSettings]);

    // ── Синхронизация «код → предпросмотр»: подсвечиваем элемент под курсором
    const highlightPreviewAt = (pos) => {
        const doc = previewFrameRef.current?.contentDocument;
        if (!doc || pos === null || pos === undefined) return;
        let best = null;
        let bestIdx = -1;
        doc.querySelectorAll('[data-mt-idx]').forEach((el) => {
            const i = parseInt(el.getAttribute('data-mt-idx'), 10);
            if (!Number.isNaN(i) && i <= pos && i > bestIdx) {
                bestIdx = i;
                best = el;
            }
        });
        doc.querySelectorAll('[data-mt-hl]').forEach((el) => el.removeAttribute('data-mt-hl'));
        if (best) {
            best.setAttribute('data-mt-hl', '');
            try { best.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) { /* noop */ }
        }
    };

    const cursorSyncExtension = useMemo(() => EditorView.updateListener.of((update) => {
        if (!update.selectionSet) return;
        const pos = update.state.selection.main.head;
        lastCursorPosRef.current = pos;
        highlightPreviewAt(pos);
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Синхронизация «предпросмотр → код»: клик по элементу выделяет его тег
    const handlePreviewLoad = () => {
        const doc = previewFrameRef.current?.contentDocument;
        if (!doc) return;

        doc.addEventListener('mouseover', (e) => {
            const el = e.target?.closest ? e.target.closest('[data-mt-idx]') : null;
            doc.querySelectorAll('[data-mt-hover]').forEach((n) => n.removeAttribute('data-mt-hover'));
            if (el && el.tagName !== 'BODY' && el.tagName !== 'HTML') el.setAttribute('data-mt-hover', '');
        });

        doc.addEventListener('click', (e) => {
            const el = e.target?.closest ? e.target.closest('[data-mt-idx]') : null;
            if (!el) return;
            e.preventDefault();
            const idx = parseInt(el.getAttribute('data-mt-idx'), 10);
            if (Number.isNaN(idx)) return;

            doc.querySelectorAll('[data-mt-hl]').forEach((n) => n.removeAttribute('data-mt-hl'));
            el.setAttribute('data-mt-hl', '');

            const view = htmlEditorViewRef.current;
            if (!view || view.destroyed) return;
            const src = view.state.doc.toString();
            const safeIdx = Math.min(idx, src.length);
            const gt = src.indexOf('>', safeIdx);
            const head = gt === -1 ? Math.min(safeIdx + 1, src.length) : Math.min(gt + 1, src.length);
            view.dispatch({
                selection: { anchor: safeIdx, head },
                scrollIntoView: true
            });
        });

        // После перезагрузки iframe (каждое изменение кода) восстанавливаем подсветку
        if (lastCursorPosRef.current !== null) {
            highlightPreviewAt(lastCursorPosRef.current);
        }
    };

    const showCodePane = viewMode !== 'preview';
    const showPreviewPane = viewMode !== 'code';

    const missingParts = [
        !formData.subject && 'тема письма',
        !formData.id && 'ID шаблона',
        !hasTrigger && 'условие отправки'
    ].filter(Boolean);

    return (
        <div
            className={s.modalOverlay}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={s.modalContent} onMouseDown={(e) => e.stopPropagation()}>
                <div className={s.modalHeader}>
                    <div className={s.modalHeaderText}>
                        <h3 className={s.modalTitle}>
                            <span className={s.titleIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                                    <line x1="8" y1="8" x2="16" y2="8"></line>
                                    <line x1="8" y1="12" x2="16" y2="12"></line>
                                </svg>
                            </span>
                            <span>{isCreate ? 'Новый шаблон письма' : 'Редактор шаблона'}</span>
                            {!isCreate && formData.name && <span className={s.modalTitleName}>{formData.name}</span>}
                        </h3>
                        <p className={s.modalSubtitle}>Слева — настройки и условия отправки, справа — код письма и живой предпросмотр</p>
                    </div>
                    <button className={s.modalClose} onClick={onClose}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <form className={s.editorForm} onSubmit={onSubmit}>
                    <div className={s.modalMain}>
                        {/* ── Левая панель: настройки и условия отправки */}
                        <aside className={s.sidebar}>
                            <section className={s.sideSection}>
                                <div className={s.sideTitle}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                    </svg>
                                    <span>Основное</span>
                                </div>
                                <div className={s.field}>
                                    <label className={s.fieldLabel}>Название</label>
                                    <input
                                        type="text"
                                        className={s.input}
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Например: Письмо комиссии"
                                    />
                                    <span className={s.fieldHint}>Служебное имя — видно только в этом разделе</span>
                                </div>
                                <div className={s.field}>
                                    <label className={s.fieldLabel}>Тема письма <span className={s.required}>*</span></label>
                                    <input
                                        type="text"
                                        className={s.input}
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        placeholder="Например: Confirmación de retiro"
                                    />
                                    <span className={s.fieldHint}>Заголовок, который увидит получатель. Поддерживает переменные</span>
                                </div>
                                <div className={s.field}>
                                    <label className={s.fieldLabel}>ID шаблона <span className={s.required}>*</span></label>
                                    <input
                                        type="text"
                                        className={`${s.input} ${s.inputMono}`}
                                        value={formData.id}
                                        onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                                        placeholder="template_1234567890"
                                    />
                                    <span className={s.fieldHint}>Технический идентификатор — генерируется автоматически</span>
                                </div>
                            </section>

                            <section className={s.sideSection}>
                                <div className={s.sideTitle}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                                        <line x1="9" y1="4" x2="9" y2="20"></line>
                                    </svg>
                                    <span>Формат содержимого</span>
                                </div>
                                <div className={s.typeSwitch}>
                                    <button
                                        type="button"
                                        className={`${s.typeOption} ${isHtml ? s.typeOptionActive : ''}`}
                                        onClick={() => setFormData({ ...formData, type: 'html' })}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 18l6-6-6-6"></path>
                                            <path d="M8 6l-6 6 6 6"></path>
                                        </svg>
                                        <span>HTML</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${s.typeOption} ${!isHtml ? s.typeOptionActive : ''}`}
                                        onClick={() => setFormData({ ...formData, type: 'text' })}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="4" y1="7" x2="20" y2="7"></line>
                                            <line x1="4" y1="12" x2="20" y2="12"></line>
                                            <line x1="4" y1="17" x2="14" y2="17"></line>
                                        </svg>
                                        <span>Текст</span>
                                    </button>
                                </div>
                                <span className={s.fieldHint}>
                                    {isHtml
                                        ? 'Полноценная HTML-вёрстка письма с живым предпросмотром'
                                        : 'Простое текстовое письмо без вёрстки'}
                                </span>
                            </section>

                            <section className={s.sideSection}>
                                <div className={s.sideTitle}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 2 11 13"></path>
                                        <path d="M22 2 15 22 11 13 2 9 22 2z"></path>
                                    </svg>
                                    <span>Когда отправляется</span>
                                </div>
                                <p className={s.sideNote}>Выберите хотя бы одно условие — оно определяет, в какой момент клиенту уйдёт это письмо.</p>

                                <div className={s.triggerRow}>
                                    <div className={s.triggerText}>
                                        <span className={s.triggerTitle}>При регистрации</span>
                                        <span className={s.triggerHint}>Клиент создал аккаунт — уходит письмо с кодом подтверждения</span>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={formData.useOnRegistration}
                                        className={`${s.switch} ${formData.useOnRegistration ? s.switchOn : ''}`}
                                        onClick={() => setFormData({ ...formData, useOnRegistration: !formData.useOnRegistration })}
                                    >
                                        <span className={s.switchThumb} />
                                    </button>
                                </div>

                                <div className={s.triggerRow}>
                                    <div className={s.triggerText}>
                                        <span className={s.triggerTitle}>При успешном выводе</span>
                                        <span className={s.triggerHint}>Вывод средств завершён успешно</span>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={formData.useOnSuccess}
                                        className={`${s.switch} ${formData.useOnSuccess ? s.switchOn : ''}`}
                                        onClick={() => setFormData({ ...formData, useOnSuccess: !formData.useOnSuccess })}
                                    >
                                        <span className={s.switchThumb} />
                                    </button>
                                </div>

                                <div className={s.triggerDivider}>
                                    <span>или по быстрому действию</span>
                                </div>

                                <div className={s.field}>
                                    <div className={s.ddWrapper}>
                                        <button
                                            type="button"
                                            className={`${s.ddButton} ${formData.actionKey ? s.ddButtonActive : ''}`}
                                            onClick={() => setShowActionKeyDd(!showActionKeyDd)}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                                            </svg>
                                            <span className={!formData.actionKey ? s.ddPlaceholder : ''}>
                                                {actionKeys.find(k => k.value === formData.actionKey)?.label || 'Быстрое действие не выбрано'}
                                            </span>
                                            <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points={showActionKeyDd ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                                            </svg>
                                        </button>
                                        {showActionKeyDd && (
                                            <div className={s.ddMenu}>
                                                <button type="button" className={`${s.ddOption} ${!formData.actionKey ? s.ddOptionActive : ''}`} onClick={() => { setFormData({ ...formData, actionKey: '' }); setShowActionKeyDd(false); }}>
                                                    <span>Не привязывать</span>
                                                    {!formData.actionKey && <span className={s.ddCheck}>✓</span>}
                                                </button>
                                                {actionKeys.map(key => (
                                                    <button key={key.value} type="button" className={`${s.ddOption} ${formData.actionKey === key.value ? s.ddOptionActive : ''}`} onClick={() => { setFormData({ ...formData, actionKey: key.value }); setShowActionKeyDd(false); }}>
                                                        <span>{key.label}</span>
                                                        {formData.actionKey === key.value && <span className={s.ddCheck}>✓</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <span className={s.fieldHint}>
                                        Письмо уйдёт автоматически, когда сапп нажмёт эту кнопку быстрого действия в админ-панели (комиссия, верификация и т.д.)
                                    </span>
                                </div>

                                {!hasTrigger && (
                                    <div className={s.triggerWarn}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                            <line x1="12" y1="9" x2="12" y2="13"></line>
                                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                        </svg>
                                        <span>Условие не выбрано — без него шаблон не сохранится</span>
                                    </div>
                                )}
                            </section>

                            <section className={s.sideSection}>
                                <div className={s.sideTitle}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="4"></circle>
                                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"></path>
                                    </svg>
                                    <span>Отправитель</span>
                                </div>
                                <p className={s.sideNote}>С какого адреса клиенту придёт это письмо.</p>
                                <div className={s.field}>
                                    <div className={s.ddWrapper}>
                                        <button
                                            type="button"
                                            className={`${s.ddButton} ${formData.fromDomain ? s.ddButtonActive : ''}`}
                                            onClick={() => setShowSenderDd(!showSenderDd)}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                                            </svg>
                                            <span>{currentSender.label}</span>
                                            <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points={showSenderDd ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                                            </svg>
                                        </button>
                                        {showSenderDd && (
                                            <div className={s.ddMenu}>
                                                {senderOptions.map(opt => (
                                                    <button
                                                        key={opt.value || 'default'}
                                                        type="button"
                                                        className={`${s.ddOption} ${(formData.fromDomain || '') === opt.value ? s.ddOptionActive : ''}`}
                                                        onClick={() => { setFormData({ ...formData, fromDomain: opt.value }); setShowSenderDd(false); }}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {(formData.fromDomain || '') === opt.value && <span className={s.ddCheck}>✓</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <span className={s.fieldHint}>
                                        {formData.fromDomain
                                            ? 'Альтернативный домен — письмо уйдёт через Resend'
                                            : 'Основной домен, используется для всех писем по умолчанию'}
                                    </span>
                                </div>
                            </section>

                            {!loadingConfigs && availableConfigs.length > 0 && (
                                <section className={s.sideSection}>
                                    <div className={s.sideTitle}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        <span>Доступ по конфигам</span>
                                        {(formData.allowedConfigs || []).length > 0 && (
                                            <span className={s.sideCounter}>{formData.allowedConfigs.length}</span>
                                        )}
                                    </div>
                                    <p className={s.sideNote}>Ничего не выбрано — шаблон доступен во всех конфигах.</p>
                                    {availableConfigs.length >= 5 && (
                                        <div className={s.cfgSearch}>
                                            <svg className={s.cfgSearchIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                                <circle cx="11" cy="11" r="7" />
                                                <line x1="21" y1="21" x2="16.5" y2="16.5" />
                                            </svg>
                                            <input
                                                className={s.cfgSearchInput}
                                                value={cfgQuery}
                                                onChange={(e) => setCfgQuery(e.target.value)}
                                                placeholder="Поиск конфига или страны…"
                                            />
                                            {cfgQuery && (
                                                <button type="button" className={s.cfgSearchClear} onClick={() => setCfgQuery('')} aria-label="Очистить">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <div className={s.configsList}>
                                        {cfgGroups.length === 0 && (
                                            <div className={s.cfgEmpty}>Ничего не найдено</div>
                                        )}
                                        {cfgGroups.map((g) => {
                                            // Во время поиска все совпавшие группы раскрыты
                                            const isOpen = cfgQ ? true : cfgExpanded.has(g.code);
                                            const selCount = g.items.filter((it) => (formData.allowedConfigs || []).includes(it.filename)).length;
                                            return (
                                                <div key={g.code} className={s.cfgGroup}>
                                                    <button type="button" className={s.cfgGroupHead} onClick={() => toggleCfgGroup(g.code)}>
                                                        <FlagIcon code={g.code} className={s.cfgFlag} alt="" />
                                                        <span className={s.cfgGroupLabel}>{g.label}</span>
                                                        {selCount > 0 && <span className={s.cfgGroupSel}>{selCount}</span>}
                                                        <span className={s.cfgGroupCount}>{g.items.length}</span>
                                                        <svg className={`${s.cfgChevron} ${isOpen ? s.cfgChevronOpen : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                    {isOpen && (
                                                        <div className={s.cfgGroupItems}>
                                                            {g.items.map((it) => {
                                                                const isChecked = (formData.allowedConfigs || []).includes(it.filename);
                                                                return (
                                                                    <label key={it.filename} className={s.configCheckbox}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isChecked}
                                                                            onChange={() => onConfigToggle(it.filename)}
                                                                        />
                                                                        <span>{it.name}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}
                        </aside>

                        {/* ── Рабочая область: код + предпросмотр */}
                        <div className={s.workspace}>
                            <div className={s.workToolbar}>
                                <div className={s.viewSwitch}>
                                    <button
                                        type="button"
                                        className={`${s.viewTab} ${viewMode === 'code' ? s.viewTabActive : ''}`}
                                        onClick={() => setViewMode('code')}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 18l6-6-6-6"></path>
                                            <path d="M8 6l-6 6 6 6"></path>
                                        </svg>
                                        <span>Код</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${s.viewTab} ${viewMode === 'split' ? s.viewTabActive : ''}`}
                                        onClick={() => setViewMode('split')}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                                            <line x1="12" y1="3" x2="12" y2="21"></line>
                                        </svg>
                                        <span>Сплит</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${s.viewTab} ${viewMode === 'preview' ? s.viewTabActive : ''}`}
                                        onClick={() => setViewMode('preview')}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                        <span>Предпросмотр</span>
                                    </button>
                                </div>
                                <div className={s.toolbarRight}>
                                    {showPreviewPane && (
                                        <button
                                            type="button"
                                            className={`${s.toolChip} ${sampleData ? s.toolChipOn : ''}`}
                                            onClick={() => setSampleData(!sampleData)}
                                            title="Подставить примерные значения переменных в предпросмотр"
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 3v2M12 19v2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M3 12h2M19 12h2M5.6 18.4 7 17M17 7l1.4-1.4"></path>
                                                <circle cx="12" cy="12" r="4"></circle>
                                            </svg>
                                            <span>Пример данных</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className={`${s.toolChip} ${showVars ? s.toolChipOn : ''}`}
                                        onClick={() => {
                                            // Вставка идёт в код — если открыт только предпросмотр, показываем сплит
                                            if (!showVars && viewMode === 'preview') setViewMode('split');
                                            setShowVars(!showVars);
                                        }}
                                        title="Панель переменных — клик вставляет переменную в код"
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 18l6-6-6-6"></path>
                                            <path d="M8 6l-6 6 6 6"></path>
                                        </svg>
                                        <span>Переменные</span>
                                    </button>
                                </div>
                            </div>

                            <div className={s.workArea}>
                                {showCodePane && (
                                    <div className={s.editorPane}>
                                        {isHtml ? (
                                            <CodeMirror
                                                value={formData.html || ''}
                                                height="100%"
                                                extensions={[htmlLanguage(), cursorSyncExtension]}
                                                theme={isDarkEditor ? 'dark' : 'light'}
                                                onCreateEditor={(view) => {
                                                    htmlEditorViewRef.current = view;
                                                }}
                                                onChange={(value) => setFormData({ ...formData, html: value })}
                                                basicSetup={{
                                                    lineNumbers: true,
                                                    foldGutter: true,
                                                    highlightActiveLine: true,
                                                    highlightSelectionMatches: true
                                                }}
                                            />
                                        ) : (
                                            <CodeMirror
                                                value={formData.content || ''}
                                                height="100%"
                                                theme={isDarkEditor ? 'dark' : 'light'}
                                                onCreateEditor={(view) => {
                                                    textEditorViewRef.current = view;
                                                }}
                                                onChange={(value) => setFormData({ ...formData, content: value })}
                                                basicSetup={{
                                                    lineNumbers: true,
                                                    foldGutter: true,
                                                    highlightActiveLine: true,
                                                    highlightSelectionMatches: true
                                                }}
                                            />
                                        )}
                                    </div>
                                )}

                                {showPreviewPane && (
                                    <div className={s.previewPane}>
                                        <div className={s.previewBar}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                                <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                                <path d="m3 7 9 6 9-6"></path>
                                            </svg>
                                            <span className={s.previewSubjectLabel}>Тема:</span>
                                            <span className={s.previewSubjectValue}>{previewSubject || '—'}</span>
                                            {isHtml && (
                                                <span className={s.previewSync} title="Клик по элементу в предпросмотре выделит его код. Курсор в коде подсветит элемент здесь.">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="15 3 21 3 21 9"></polyline>
                                                        <polyline points="9 21 3 21 3 15"></polyline>
                                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </svg>
                                                    <span>Синхронизация с кодом</span>
                                                </span>
                                            )}
                                        </div>
                                        {isHtml ? (
                                            <iframe
                                                ref={previewFrameRef}
                                                className={s.previewFrame}
                                                title="Предпросмотр письма"
                                                sandbox="allow-same-origin"
                                                srcDoc={previewDoc}
                                                onLoad={handlePreviewLoad}
                                            />
                                        ) : (
                                            <pre className={s.previewTextBody}>{previewContent || 'Письмо пока пустое — начните печатать в редакторе'}</pre>
                                        )}
                                    </div>
                                )}

                                {showVars && (
                                    <VariablesPanel
                                        groups={variableGroups}
                                        onInsert={insertVariable}
                                        onClose={() => setShowVars(false)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Футер: статус готовности + действия */}
                    <footer className={s.modalFooter}>
                        <div className={s.footNote}>
                            {canSave ? (
                                <span className={s.footReady}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="9 12 11 14 15 10"></polyline>
                                    </svg>
                                    <span>Всё заполнено — можно сохранять</span>
                                </span>
                            ) : (
                                <span className={s.footMissing}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                    </svg>
                                    <span>Не хватает: {missingParts.join(', ')}</span>
                                </span>
                            )}
                        </div>
                        <div className={s.footActions}>
                            <button type="button" className={s.cancelButton} onClick={onClose}>
                                <span>Отмена</span>
                            </button>
                            <button type="submit" className={s.saveButton} disabled={!canSave}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                                    <polyline points="7 3 7 8 15 8"></polyline>
                                </svg>
                                <span>Сохранить шаблон</span>
                            </button>
                        </div>
                    </footer>
                </form>
            </div>
        </div>
    );
};

export default MailTab;
